/**
 * `melee-pursuer`: close to contact, spread around the target, and swing
 * (PRD #793).
 *
 * This is the behavior the dungeon already had, lifted out of the step loop and
 * given one thing it never had: a **place to stand**. A pack of pursuers used to
 * route to the player's exact cell, which meant every one of them wanted the
 * same tile, and what a player saw was a stack. Here each pursuer claims a slot
 * on a ring around its target, so a group surrounds rather than piles, an area
 * ability has something to hit, and a fight is readable.
 *
 * Slots are assigned by {@link approachSlot} without drawing from the rng: every
 * pursuer engaged on the same target runs the same greedy assignment in entity
 * id order and claims the free slot nearest its own bearing, so each monster
 * reaches the same answer about all of them and nobody has to be told. That also
 * means a pursuer approaching from the west stays west rather than walking
 * around to a slot a counter handed it.
 *
 * **Rng draws: none.** Every tie is broken by entity id or by bearing.
 */

import type { AbilityDefinition } from '../abilityCatalog';
import { currentTarget, rememberTarget } from './perception';
import type {
  Brain,
  BrainDecision,
  BrainMemory,
  BrainPerception,
  BrainPoint,
  PerceivedAlly,
} from './types';

/**
 * How far from its target a pursuer wants to stand, in tiles.
 *
 * One tile is the dungeon's melee stopping range, which is the distance the
 * locomotion layer already refuses to close past, so the ring the brain asks for
 * and the distance the steering layer enforces agree.
 */
export const MELEE_APPROACH_RADIUS_TILES = 1;

/** Slots on the ring. Eight is the dungeon's own eight-way movement grid. */
export const MELEE_APPROACH_SLOTS = 8;

/**
 * Float slack on the contact test, in tiles.
 *
 * A pursuer stops *at* its ring radius, so the distance it then measures is
 * that radius plus or minus the last floating-point step. Without the slack a
 * monster standing exactly where it meant to stand can measure a hair too far
 * and never swing.
 */
const CONTACT_TOLERANCE_TILES = 1e-7;

function angleOf(from: BrainPoint, to: BrainPoint): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

/** Shortest angular distance between two bearings, in radians. */
function angularDistance(left: number, right: number): number {
  const delta = Math.abs(left - right) % (Math.PI * 2);
  return delta > Math.PI ? Math.PI * 2 - delta : delta;
}

/**
 * The point on the ring around `target` this monster should approach.
 *
 * Every pursuer engaged on the same target, this one included, is sorted by
 * entity id and claims the unclaimed slot closest to the bearing it already
 * approaches from. The assignment is a pure function of positions and ids, so
 * every pursuer computes the identical outcome and no coordinator is needed.
 */
export function approachSlot(
  self: { id: number; position: BrainPoint },
  targetId: number,
  targetPosition: BrainPoint,
  allies: readonly PerceivedAlly[],
): BrainPoint {
  const participants = [
    { id: self.id, position: self.position },
    ...allies.filter((ally) => ally.target === targetId && ally.id !== self.id),
  ].sort((left, right) => left.id - right.id);
  const claimed = new Set<number>();
  let slot = 0;
  for (const participant of participants) {
    const bearing = angleOf(targetPosition, participant.position);
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let candidate = 0; candidate < MELEE_APPROACH_SLOTS; candidate += 1) {
      if (claimed.has(candidate) && claimed.size < MELEE_APPROACH_SLOTS) continue;
      const distance = angularDistance(
        bearing,
        (candidate * Math.PI * 2) / MELEE_APPROACH_SLOTS,
      );
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    claimed.add(best);
    if (participant.id === self.id) slot = best;
  }
  const angle = (slot * Math.PI * 2) / MELEE_APPROACH_SLOTS;
  return {
    x: targetPosition.x + Math.cos(angle) * MELEE_APPROACH_RADIUS_TILES,
    y: targetPosition.y + Math.sin(angle) * MELEE_APPROACH_RADIUS_TILES,
  };
}

export const meleePursuer: Brain = {
  id: 'melee-pursuer',

  loadoutProblems(abilities: readonly AbilityDefinition[]): string[] {
    return abilities.some((ability) => ability.counterplay === 'melee')
      ? []
      : ['melee-pursuer needs an ability with melee counterplay to swing'];
  },

  decide(perception: BrainPerception, memory: BrainMemory): BrainDecision {
    const remembered = rememberTarget(memory, perception);
    const intent = {
      target: remembered.target,
      desiredRangeTiles: MELEE_APPROACH_RADIUS_TILES,
    };
    if (perception.casting) {
      return {
        memory: remembered,
        intent: { ...intent, move: { kind: 'hold' }, cast: null },
      };
    }
    if (remembered.target === null || !remembered.lastKnown) {
      return {
        memory: remembered,
        intent: { ...intent, move: { kind: 'idle' }, cast: null },
      };
    }
    const target = currentTarget(perception, remembered);
    const swing = perception.abilities.find(
      (ability) => ability.ready && ability.counterplay === 'melee',
    );
    // Contact is where the pursuer meant to stand, not how far the swing can
    // select: the ability's radius is deliberately a little generous so that a
    // body stopped on the ring is inside it rather than a floating-point hair
    // outside, and swinging at that outer edge would let a monster hit from
    // further out than it ever walks to.
    //
    // It is also a perception question rather than a distance one. Standing a
    // tile away through the corner of a wall is not standing in reach, and
    // `reachable` is the walkability answer the caller supplies.
    if (
      swing &&
      target &&
      target.visible &&
      target.reachable &&
      target.distanceTiles <=
        MELEE_APPROACH_RADIUS_TILES + CONTACT_TOLERANCE_TILES &&
      target.distanceTiles <= swing.rangeTiles
    ) {
      return {
        memory: remembered,
        intent: {
          ...intent,
          move: { kind: 'hold' },
          cast: { abilityId: swing.id, aim: { ...target.position } },
        },
      };
    }
    // Out of reach, or waiting on the swing: close on the ring slot. A target
    // it cannot currently see is walked to by its last known position instead,
    // which is what carries a pursuit around a corner - and it is the memory
    // rather than the live position deliberately, because a monster that
    // steered by a body it cannot see would be tracking through the wall.
    const goal = target?.visible
      ? approachSlot(
          { id: perception.self.id, position: perception.self.position },
          target.id,
          target.position,
          perception.allies,
        )
      : { ...remembered.lastKnown };
    return {
      memory: remembered,
      intent: { ...intent, move: { kind: 'approach', point: goal }, cast: null },
    };
  },
};
